import { Button, makeStyles, Skeleton, SkeletonItem } from "@fluentui/react-components";
import { useEffect, useState } from "react";
import tagService from "../../services/TagService";
import Status from "../../enums/Status";
import IBlogTag from "../../models/blog/IBlogTag";
import { dolphinTokens } from "../../utils/Theme";

interface ICategoriesProps {
    selectedCategories: string[];
    setSelectedCategories: (categories: string[]) => void;
}

const useStyles = makeStyles({
    root: {
        display: "block",
        width: "100%",
        background: dolphinTokens.colorDarkBlue
    },
    container: {
        display: "flex",
        flexWrap: "wrap",
        rowGap: "8px",
        columnGap: "8px",
        padding: "32px",
        maxWidth: "1200px",
        justifyContent: "center",
        margin: "0 auto",

        "@media screen and (max-width: 768px)": {
            flexWrap: "nowrap",
            overflowX: "auto",
            justifyContent: "flex-start",
        }
    },
    skeletons: {
        display: "flex",
        flexDirection: "row",
        columnGap: "12px",
        rowGap: "8px",
    },
    skeleton: {
        width: "96px",
        height: "32px",
    },
    skeletonItem: {
        height: "100%",
        width: "100%",
        borderRadius: "10000px !important",
    },
    unselected: {
        color: "white !important",
    }
});

export default function Categories(props: ICategoriesProps) {
    const styles = useStyles();

    const [status, setStatus] = useState<Status>(Status.LOADING);
    const [categories, setCategories] = useState<IBlogTag[]>([]);

    useEffect(() => {
        fetchCategories(setCategories, setStatus);
    }, []);

    if (
        status === Status.LOAD_ERROR ||
        (status === Status.LOAD_SUCCESS && categories.length === 0)
    ) {
        return null;
    }

    const handleCategoryChange = (category: string) => {
        const selected = props.selectedCategories.includes(category);

        const newSelectedCategories = selected
            ? props.selectedCategories.filter((c) => c !== category)
            : [...props.selectedCategories, category];

        props.setSelectedCategories(newSelectedCategories);
    };

    return (
        <div className={styles.root}>
            <div className={styles.container}>
                {status === Status.LOADING && (
                    <div className={styles.skeletons}>
                        {Array.from({ length: 3 }).map((_, index) => (
                            <Skeleton className={styles.skeleton} key={index}>
                                <SkeletonItem
                                    className={styles.skeletonItem}
                                    shape="rectangle"
                                />
                            </Skeleton>
                        ))}
                    </div>
                )}

                {status === Status.LOAD_SUCCESS &&
                    categories
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((category) => {
                            const isSelected =
                                props.selectedCategories.includes(category.name);

                            const btnAppearance = isSelected
                                ? "secondary"
                                : "outline";

                            return (
                                <Button
                                    key={category.id}
                                    shape="circular"
                                    appearance={btnAppearance}
                                    onClick={() => handleCategoryChange(category.name)}
                                    className={
                                        isSelected ? "" : styles.unselected
                                    }
                                >
                                    {category.name}
                                </Button>
                            );
                        })}
            </div>
        </div>
    );
}

async function fetchCategories(
    setCategories: (categories: IBlogTag[]) => void,
    setStatus: (status: Status) => void
) {
    try {
        const data = await tagService.getTags();

        setCategories(data);
        setStatus(Status.LOAD_SUCCESS);
    } catch {
        setStatus(Status.LOAD_ERROR);
    }
}